<template>
  <div>
    <h1>
      Your Applications
    </h1>
    <h2 class="pb-5">
      Keep track of your applications for birth records, benefits, and more.
    </h2>
  </div>

  <div v-if="!activeApplications || !draftApplications" style="text-align: center">
    <LoadingSpinner />
  </div>
  <div v-else class="cards">
    <!-- First, show active applications -->
    <ApplicationStatusCard v-for="(application, idx) in activeApplications.applications" :key="idx"
      :application="application"
      @changed="refreshApplications()"
      class="mb-4"/>

    <!-- Next, show key documents not relating to one of the main application types -->
    <KeyDocumentStatusCard v-for="(keyDocEntry, idx) in keyDocumentsToDisplay" :key="idx"
      :keyDocumentType="keyDocEntry.keyDocumentType" :keyDocument="keyDocEntry.keyDocument"
      class="mb-4"/>

    <!-- Next, show applications which can be started / drafts which can be resumed -->
    <ApplicationCreationCard v-for="(application, idx) in applicationsAvailableToCreate" :key="idx"
      :name="application.name" :description="application.description" :applicationType="application.applicationType"
      :isDraft="application.isDraft" :isTabletOnly="application.isTabletOnly"
      @changed="refreshApplications()"
      class="mb-4"/>
  </div>
  
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { CitizenApplicationsStatus } from './model';
import ApplicationStatusCard from './ApplicationStatusCard.vue';
import ApplicationCreationCard from "./ApplicationCreationCard.vue";
import KeyDocumentStatusCard from "./KeyDocumentStatusCard.vue";
import { DraftApplicationHeader } from '../applications/model';
import { isModuleEnabled, sessionState } from "@/core/session";
import { getApplicationStatus, getDraftApplications } from "./api";
import { Core } from "@minicity/core";

const activeApplications = ref<CitizenApplicationsStatus|null>(null);
const draftApplications = ref<DraftApplicationHeader[]|null>(null);

const refreshApplications = () => {
  activeApplications.value = null;
  draftApplications.value = null;
  getApplicationStatus(sessionState.session!.user.id).then((status) => activeApplications.value = status);
  getDraftApplications(sessionState.session!.user.id).then((drafts) => draftApplications.value = drafts);
};

onMounted(() => {
  refreshApplications();
});

const keyDocumentsToDisplay = computed(() => {
  const results = [];
  for (const keyDocumentType in sessionState.session?.user.keyDocuments) {
    if (["BIRTH_RECORD", "SOCIAL_SECURITY", "GEORGIA_DFCS_BENEFITS"].includes(keyDocumentType)) continue;

    const keyDocument = sessionState.session?.user.keyDocuments[keyDocumentType];
    if (!keyDocument) continue;

    if (!["REQUIRED","APPLIED","OBTAINED"].includes(keyDocument.status)) continue;
    
    results.push({keyDocumentType: keyDocumentType, keyDocument: keyDocument});
  }

  return results;
})

interface StartableApplication {
  name: string;
  description: string;
  applicationType: Core.EntityType | "identification";
  isDraft: boolean;
  isTabletOnly: boolean;
}

// Decide which new / draft application options to display
const applicationsAvailableToCreate = computed(() => {
  if (!draftApplications.value || !activeApplications.value) return [];

  const result:StartableApplication[] = [];

  const birthRecordsEnabled = isModuleEnabled('BIRTH_RECORDS');
  const socialSecurityEnabled = isModuleEnabled('SOCIAL_SECURITY');
  const benefitsEnabled = isModuleEnabled('GEORGIA_DFCS_BENEFITS');

  const draftIdApplication = draftApplications.value.find(draft => draft.applicationType=='identification');
  const draftBenefitsApplication = draftApplications.value.find(draft => draft.applicationType=='georgiaDfcsBenefits');

  const activeBirthRecordsApplication = activeApplications.value.applications.find(appl => appl.applicationType==Core.EntityType["Birth Record"]);
  const activeSocialSecurityApplication = activeApplications.value.applications.find(appl => [Core.EntityType["Social Security"], Core.EntityType["Online Social Security"]].includes(appl.applicationType));
  const activeBenefitsApplication = activeApplications.value.applications.find(appl => appl.applicationType==Core.EntityType["Georgia DFCS Benefits"]);
  
  const birthRecordsPending = birthRecordsEnabled && !activeBirthRecordsApplication;
  const socialSecurityPending = socialSecurityEnabled && !activeSocialSecurityApplication;
  const benefitsPending = benefitsEnabled && !activeBenefitsApplication;

  const legacySocialSecurityPending = socialSecurityPending && !isModuleEnabled('ONLINE_SOCIAL_SECURITY_APPLICATIONS');
  
  if (birthRecordsPending || legacySocialSecurityPending) {
    let idApplicationName;
    let idApplicationDescription;
    if (birthRecordsPending && legacySocialSecurityPending) {
      idApplicationName = "ID Application";
      idApplicationDescription = "Apply for Birth Records and/or a Social Security card";
    } else if (birthRecordsPending) {
      idApplicationName = "Birth Records Application";
      idApplicationDescription = "Apply for Birth Records";
    } else {
      idApplicationName = "Social Security Card Application";
      idApplicationDescription = "Apply for a Social Security card";
    }

    result.push({
      name: idApplicationName,
      description: idApplicationDescription,
      applicationType: "identification",
      isDraft: draftIdApplication != undefined,
      isTabletOnly: false
    });
  }

  if (socialSecurityPending && !legacySocialSecurityPending) {
    result.push({
      name: "Social Security Card Application",
      description: "Apply for a Social Security card",
      applicationType: Core.EntityType['Online Social Security'],
      isDraft: false,
      isTabletOnly: false
    });
  }

  if (benefitsPending) {
    result.push({
      name: "GA Benefits Application",
      description: "Apply for TANF / Food Stamps, Medicaid, and more",
      applicationType: Core.EntityType['Georgia DFCS Benefits'],
      isDraft: draftBenefitsApplication != undefined,
      isTabletOnly: false
    });
  }

  return result;
});
</script>

<style scoped>
.cards {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
